import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, ListVideo, RefreshCw } from 'lucide-react';
import { useSignagePlaylists } from '@/hooks/signage';
import { PlaylistCard } from './PlaylistCard';
import { CreatePlaylistDialog } from './CreatePlaylistDialog';
import { PlaylistBuilderDialog } from './PlaylistBuilderDialog';

interface PlaylistListProps {
  businessId: string;
}

export function PlaylistList({ businessId }: PlaylistListProps) {
  const {
    playlists,
    loading,
    fetchPlaylists,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
  } = useSignagePlaylists(businessId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPlaylistId, setEditingPlaylistId] = useState<string | null>(null);

  const handleCreate = async (data: { name: string; description?: string }) => {
    const result = await createPlaylist(data);
    if (result) {
      setIsCreateOpen(false);
      // Open the builder for the new playlist
      setEditingPlaylistId(result.id);
    }
  };

  const handleDelete = async (playlistId: string) => {
    await deletePlaylist(playlistId);
  };

  const handleToggleActive = async (playlistId: string, isActive: boolean) => {
    await updatePlaylist(playlistId, { is_active: isActive });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-5 w-32 bg-muted rounded" />
                <div className="h-4 w-24 bg-muted rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-16 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Playlists</h2>
          <p className="text-sm text-muted-foreground">
            {playlists.length} playlist{playlists.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPlaylists}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Playlist
          </Button>
        </div>
      </div>

      {/* Playlist Grid */}
      {playlists.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <ListVideo className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg mb-2">No playlists yet</CardTitle>
            <CardDescription className="text-center max-w-sm mb-4">
              Create a playlist to group content together and rotate it on your displays.
            </CardDescription>
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Playlist
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((playlist) => (
            <PlaylistCard
              key={playlist.id}
              playlist={playlist}
              onEdit={() => setEditingPlaylistId(playlist.id)}
              onDelete={() => handleDelete(playlist.id)}
              onToggleActive={(active) => handleToggleActive(playlist.id, active)}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <CreatePlaylistDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={handleCreate}
      />

      {/* Builder Dialog */}
      {editingPlaylistId && (
        <PlaylistBuilderDialog
          open={!!editingPlaylistId}
          onOpenChange={(open) => !open && setEditingPlaylistId(null)}
          playlistId={editingPlaylistId}
          businessId={businessId}
          onSaved={fetchPlaylists}
        />
      )}
    </div>
  );
}
